import { disc } from '@jsamr/counter-style/presets'
import MarkedList from '@jsamr/react-native-li'
import React from 'react'

import { Box } from 'widgets/Box'
import { ButtonProps } from 'widgets/Button'
import { Modal } from 'widgets/Modal'
import { Text } from 'widgets/Text'

import { useGroup } from 'hooks/useGroup'
import { useGroupWithPrices } from 'hooks/useGroupWithPrices'
import { useStripe } from 'hooks/useStripe'

import { openExternalLink } from 'utils/links'
import { centsToDollars } from 'utils/numbers'

import { PartialUser } from 'types/user'

type Props = {
  trainer?: PartialUser
  handleClose: () => void
  isSubscribed: boolean
  open: boolean
}

export const SubscriptionModal = ({ trainer, handleClose, isSubscribed, open }: Props) => {
  const { createCheckoutSession, customerPortal } = useStripe()
  const { data: groupWithPrices } = useGroupWithPrices(trainer?.id)
  const { meta } = useGroup({ userId: trainer?.id })
  const { hasSetupGroup } = meta

  const selectedPrice = groupWithPrices?.prices?.[0]
  const title = isSubscribed ? `You are subscribed to ${trainer?.firstName}` : 'Join'
  const subTitle = !isSubscribed ? `${trainer?.firstName} ${trainer?.lastName}'s Group` : undefined

  const onPressSubscribe = async () => {
    if (!selectedPrice) return

    const checkoutSession = await createCheckoutSession.mutateAsync({
      priceId: selectedPrice?.id,
      trainerId: trainer?.id,
    })

    openExternalLink(checkoutSession?.url, { newTab: false })
  }

  const openCustomerPortal = async () => {
    const newCustomerPortalLink = await customerPortal.refetch()
    openExternalLink(newCustomerPortalLink.data?.url || '', { newTab: true })
  }

  // Put subscription state, based off the trainer, here.
  const actionButtonProps: ButtonProps[] = isSubscribed
    ? [
        {
          title: 'Manage',
          type: 'v2primary',
          disabled: customerPortal.isFetching,
          onPress: openCustomerPortal,
          fullWidth: true,
        },
      ]
    : [
        {
          title: !selectedPrice || createCheckoutSession.isLoading ? 'Loading...' : 'Subscribe',
          type: 'primav2primaryryAlt',
          disabled: !selectedPrice || createCheckoutSession.isLoading,
          onPress: onPressSubscribe,
          fullWidth: true,
        },
      ]

  return (
    <Modal
      actionButtonProps={actionButtonProps}
      isVisible={open && hasSetupGroup}
      onClose={handleClose}
      title={title}
      subTitle={subTitle}
    >
      <Box padding='xs' margin={{ horizontal: 'md' }}>
        <Text bold>Monthly Cost</Text>
        {selectedPrice ? (
          <Text fontSize='md'>{centsToDollars(selectedPrice.charge)}</Text>
        ) : (
          <Text fontSize='md'>This is currently a free subscription.</Text>
        )}
      </Box>
      {groupWithPrices.description && isSubscribed && (
        <Box padding='xs' margin={{ horizontal: 'md', vertical: 'sm' }}>
          <Text bold>Description</Text>
          <Text>{groupWithPrices.description}</Text>
        </Box>
      )}
      {!isSubscribed && (
        <Box padding='xs' margin={{ horizontal: 'md', vertical: 'sm' }}>
          <Text bold>Join the community to unlock: </Text>
          <MarkedList
            counterRenderer={disc}
            // The library requires plain JS objects to be passed.
            // eslint-disable-next-line react-native/no-inline-styles
            markerTextStyle={{
              fontWeight: 'normal',
              fontSize: 14,
              fontFamily: 'Poppins',
              color: 'black',
            }}
            // The library requires plain JS objects to be passed.
            // eslint-disable-next-line react-native/no-inline-styles
            markerBoxStyle={{ marginTop: 3 }}
          >
            <Text fontSize='md'>Premium Content</Text>
            <Text fontSize='md'>A Leaderboard</Text>
            <Text fontSize='md'>Group Chat</Text>
          </MarkedList>
        </Box>
      )}
    </Modal>
  )
}
